export const defaultLocale = "en";

// 5 locales (en, ar, fr, es, de) + 1,426 SEO plans + 6 devices ≈ 19,124 files.
// Formula: Total = plans×fpp×locales + (locales-1)×454 + devices×384 + 911
export const supportedLocales = ["en", "ar", "fr", "es", "de"] as const;

export const localeLabels: Record<string, string> = {
  en: "English",
  ar: "العربية",
  fr: "Français",
  es: "Español",
  de: "Deutsch",
};

// Right-to-left locales — used by applyDocumentLang to set dir="rtl".
export const rtlLocales = new Set<string>(["ar"]);

export const localeStorageKey = "flash_preferred_locale";
export const localePromptKey = "flash_locale_prompt_dismissed";

export type SupportedLocale = (typeof supportedLocales)[number];

const fallbackLocaleMap: Record<string, SupportedLocale> = {
  // Spanish variants
  ca: "es",
  gl: "es",
  // Romance languages → nearest
  it: "fr",
  pt: "es",
  pt_br: "es",
  pt_pt: "es",
  ro: "fr",
  // Germanic
  nl: "de",
  da: "de",
  no: "de",
  sv: "de",
  pl: "de",
  cs: "de",
  sk: "de",
  // Arabic dialects
  ar_sa: "ar",
  ar_eg: "ar",
  ar_ae: "ar",
};

export const normalizeLocale = (value?: string | null): SupportedLocale | null => {
  if (!value) return null;
  const base = value.toLowerCase().split("-")[0];
  return (supportedLocales as readonly string[]).includes(base) ? (base as SupportedLocale) : null;
};

export const resolvePreferredLocale = (value?: string | null): SupportedLocale => {
  const normalized = normalizeLocale(value);
  if (normalized) return normalized;
  const base = value?.toLowerCase().split("-")[0];
  if (base && base in fallbackLocaleMap) return fallbackLocaleMap[base];
  return defaultLocale;
};

export const getBrowserLocale = (): SupportedLocale | null => {
  if (typeof navigator === "undefined") return null;
  return resolvePreferredLocale(navigator.language);
};

export const getStoredLocale = (): SupportedLocale | null => {
  if (typeof localStorage === "undefined") return null;
  try {
    const stored = localStorage.getItem(localeStorageKey);
    return normalizeLocale(stored);
  } catch {
    return null;
  }
};

export const setStoredLocale = (locale: SupportedLocale) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(localeStorageKey, locale);
    if (typeof window !== "undefined") {
      try {
        document.cookie = `${localeStorageKey}=${locale}; path=/; max-age=31536000; samesite=lax`;
      } catch {
        // ignore
      }
      window.dispatchEvent(new CustomEvent("flash_locale_changed", { detail: locale }));
    }
  } catch {
    // ignore
  }
};

export const markPromptDismissed = () => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(localePromptKey, "true");
  } catch {
    // ignore
  }
};

export const wasPromptDismissed = (): boolean => {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(localePromptKey) === "true";
  } catch {
    return false;
  }
};

export const applyDocumentLang = (locale: SupportedLocale) => {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  document.documentElement.dir = rtlLocales.has(locale) ? "rtl" : "ltr";
  document.documentElement.setAttribute("data-locale", locale);
};

const NON_LOCALIZED_PREFIXES = [
  '/checkout',
  '/success',
  '/cancel',
  '/orders',
  '/top-up',
  '/pay',
  '/api',
  '/gone',
  '/authors',
  '/devices',
  '/carriers',
  '/product',
];

/**
 * Checks if a path belongs to the English-only (default) routing group.
 * These pages only exist at the root level and do not have localized counterparts.
 * They must never be prefixed with a locale code.
 */
export function isNonLocalizedPath(href: string): boolean {
  if (!href) return false;
  // Extract the pathname part (remove query parameters and hashes)
  const cleanPath = href.split('?')[0].split('#')[0];
  
  // 1. Direct prefix checking for standard English-only routes
  const match = NON_LOCALIZED_PREFIXES.some(prefix => 
    cleanPath === prefix || cleanPath.startsWith(prefix + '/')
  );
  if (match) return true;

  // 2. Special case for /blog/[slug]/ vs /blog/ index
  // /blog/ (blog list) is localized, but /blog/[slug]/ (blog posts) is English-only.
  if (cleanPath.startsWith('/blog/')) {
    const remaining = cleanPath.slice('/blog/'.length).replace(/\/+$/, '');
    if (remaining.length > 0) {
      return true; // Contains a blog post slug, so it's English-only
    }
  }

  return false;
}

